import React from 'react';
import {
	useNavigate,
	useParams,
	Switch,
	useLocation,
	Outlet,
} from 'react-router-dom';
import {
	ArrowLeftStartOnRectangleIcon,
	BellAlertIcon,
	ClockIcon,
	CubeIcon,
	KeyIcon,
	QrCodeIcon,
	ServerIcon,
	Squares2X2Icon,
	StarIcon,
	TableCellsIcon,
	UserIcon,
	UserPlusIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { Settings as SettingsComponent } from './Settings';
import DataTable from '../../components/DataTable';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';

import { getArrayPosition } from '../../components/helpers/functions';
import useAsync from '../../hooks/useAsync';

import { getModules } from '../../api/modules';

import Modules from './Modules';
import Integrations from './Integrations';
import Integration from './Integrations/Integration';
import ArchivedAssets from './ArchivedAssets';
import NotificationSettings from './Notifications';
import QRCodeSetting from './QRCode';
import UserSettings from './UserSettings';
import AccountSettings from './Account';
import AssignmentSettings from './Assignments';

const CustomTypesTable = () => {
	return (
		<DataTable
			title="Custom Types"
			columns={[
				{
					header: 'NAME',
					accessorKey: 'name',
					cell: (info) => {
						return info.getValue() || null;
					},
				},
			]}
			hasCheckboxes={false}
			manualSorting={false}
			data={{
				rows: [
					{ name: 'Assets', fields_count: 0, updatedAt: 'Default' },
					{ name: 'Consumables', fields_count: 2, updatedAt: 'Default' },
				],
			}}
			usePagination={false}
			useSearch={false}
			useExport={false}
		/>
	);
};

export const CustomFieldsTable = () => {
	const navigate = useNavigate();
	const { value: modules } = useAsync(getModules);

	return (
		<>
			<h2 className="text-base font-semibold leading-6 text-gray-900">
				Data Structures
			</h2>
			<p className="mt-1 text-sm text-gray-500">
				Data structures are pre-built objects within the platform. They are used
				individually and combined together to bring you the most customizable
				experience possible
			</p>
			<DataTable
				columns={[
					{
						header: 'NAME',
						accessorKey: 'name',
						cell: (info) => {
							return <div className="w-36">{info.getValue() || null}</div>;
						},
					},
					{
						header: 'DESCRIPTION',
						accessorKey: 'description',
						wrap: true,
						cell: (info) => {
							return info.getValue() || null;
						},
					},
				]}
				hasCheckboxes={false}
				manualSorting={false}
				data={modules || { rows: [] }}
				usePagination={false}
				useSearch={false}
				useExport={false}
				onRow={(row) =>
					navigate(`/settings/data-structures/${row?.original?.id}`)
				}
			/>
		</>
	);
};

export const SettingsEntry = () => {
	const navigate = useNavigate();
	const location = useLocation();

	let tabParam = getArrayPosition(
		location?.pathname?.split('/').filter(Boolean),
		2,
	);
	return (
		<MainContent maxWidth="w-full">
			<PageHeading divider={false}>Settings</PageHeading>

			<div className="bg-white -mt-8 h-full">
				<div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
					<aside className="py-6 lg:col-span-3">
						<nav>
							<div className="text-base font-semibold leading-6 text-gray-900 mb-6">
								User
							</div>
							<SettingsComponent
								onClick={(id) => navigate(`/settings/${id}`)}
								menu={[
									{
										id: 'profile',
										name: 'Profile',
										current: tabParam === 'profile',
										icon: UserIcon,
									},
									{
										id: 'notifications',
										name: 'Notifications',
										current: tabParam === 'notifications',
										icon: BellAlertIcon,
									},
									{
										id: 'ux-preferences',
										name: 'UX Preferences',
										current: tabParam === 'ux-preferences',
										icon: StarIcon,
									},
									/*{
						id: 'import',
						name: 'Import/Export',
						current: tabParam === 'import',
						icon: ArrowPathRoundedSquareIcon,
					},
					{
						id: 'subscription',
						name: 'Subscription',
						current: tabParam === 'subscription',
						icon: TicketIcon,
					},
					{
						id: 'verified',
						name: 'Scipods Verified',
						current: tabParam === 'verified',
						icon: CheckBadgeIcon,
					},*/
								]}
							/>

							<div className="text-base font-semibold leading-6 text-gray-900 my-6 ">
								Platform
							</div>

							<SettingsComponent
								onClick={(id) => navigate(`/settings/${id}`)}
								menu={[
									{
										id: 'data-structures',
										name: 'Data Structures',
										current: !tabParam || tabParam === 'data-structures',
										icon: TableCellsIcon,
									},
									{
										id: 'integrations',
										name: 'Integrations',
										current: tabParam === 'integrations',
										icon: Squares2X2Icon,
									},
									{
										id: 'archived',
										name: 'Archived Assets',
										current: tabParam === 'archived',
										icon: ServerIcon,
									},

									{
										id: 'asset-tags',
										name: 'Asset Tags',
										current: tabParam === 'asset-tags',
										icon: QrCodeIcon,
									},

									{
										id: 'assignments',
										name: 'Global Reminders',
										current: tabParam === 'assignments',
										icon: ClockIcon,
									},
								{
									id: 'api-tokens',
									name: 'API Tokens',
									current: tabParam === 'api-tokens',
									icon: KeyIcon,
								},
								{
									id: 'assets',
									name: 'Assets',
									current: tabParam === 'assets',
									icon: CubeIcon,
								},

									/*{
						id: 'import',
						name: 'Import/Export',
						current: tabParam === 'import',
						icon: ArrowPathRoundedSquareIcon,
					},
					{
						id: 'subscription',
						name: 'Subscription',
						current: tabParam === 'subscription',
						icon: TicketIcon,
					},
					{
						id: 'verified',
						name: 'Scipods Verified',
						current: tabParam === 'verified',
						icon: CheckBadgeIcon,
					},*/
								]}
							/>
						</nav>
					</aside>

					<form
						className="divide-y divide-gray-200 lg:col-span-9"
						action="#"
						method="POST"
					>
						{/* Profile section */}
						<div className="py-6 px-4 sm:p-6 lg:pb-8">
							<Outlet />
							{/*<Routes>
								<ProtectedRoute
									exact
									path="/settings"
									component={CustomFieldsTable}
								/>
								<ProtectedRoute
									exact
									path="/settings/data-structures"
									component={CustomFieldsTable}
								/>
								<ProtectedRoute
									exact
									path="/settings/integrations"
									component={Integrations}
								/>
								<ProtectedRoute
									exact
									path="/settings/archived"
									component={ArchivedAssets}
								/>
								<ProtectedRoute
									exact
									path="/settings/profile"
									component={AccountSettings}
								/>
								<ProtectedRoute
									exact
									path="/settings/assignments"
									component={AssignmentSettings}
								/>
								<ProtectedRoute
									exact
									path="/settings/notifications"
									component={NotificationSettings}
								/>
								<ProtectedRoute
									exact
									path="/settings/ux-preferences"
									component={UserSettings}
								/>
								<ProtectedRoute
									exact
									path="/settings/asset-tags"
									component={QRCodeSetting}
								/>
								<ProtectedRoute
									exact
									path="/settings/user-settings"
									component={UserSettings}
								/>
							</Routes>*/}
						</div>
					</form>
				</div>
			</div>
		</MainContent>
	);
};

export default () => {};

/*
	return (
		<Routes>
			<ProtectedRoute exact path="/settings" component={Settings} />
			<ProtectedRoute
				exact
				path="/settings/data-structures"
				component={Settings}
			/>
			<ProtectedRoute
				exact
				path="/settings/integrations"
				component={Settings}
			/>
			<ProtectedRoute exact path="/settings/archived" component={Settings} />
			<ProtectedRoute exact path="/settings/profile" component={Settings} />
			<ProtectedRoute exact path="/settings/assignments" component={Settings} />
			<ProtectedRoute
				exact
				path="/settings/notifications"
				component={Settings}
			/>
			<ProtectedRoute
				exact
				path="/settings/ux-preferences"
				component={Settings}
			/>
			<ProtectedRoute exact path="/settings/asset-tags" component={Settings} />
			<ProtectedRoute
				exact
				path="/settings/user-settings"
				component={Settings}
			/>
			<ProtectedRoute
				exact
				path="/settings/data-structures/:id"
				component={Modules}
			/>
			<ProtectedRoute
				exact
				path="/settings/data-structures/:id/:tab"
				component={Modules}
			/>
			<ProtectedRoute
				exact
				path="/settings/integrations/:id"
				component={Integration}
			/>
			<ProtectedRoute
				exact
				path="/settings/integrations/:id/:tab"
				component={Integration}
			/>
		</Routes>
	);
};
*/
